export interface Skill {
  id: string
  name: string
  category: 'frontend' | 'backend' | 'blockchain' | 'data' | 'tools' | 'finance'
  level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  years?: number
  description?: string
  projects?: string[]
}

export interface SkillCategory {
  id: string
  name: string
  description: string
  skills: Skill[]
}

export const skills: Skill[] = [
  // Frontend
  {
    id: "react",
    name: "React",
    category: "frontend",
    level: "expert",
    years: 4,
    description: "Building complex, interactive user interfaces with modern React patterns",
    projects: ["DeFi Protocol", "Trading Dashboard"]
  },
  {
    id: "typescript",
    name: "TypeScript",
    category: "frontend",
    level: "expert",
    years: 3,
    description: "Strong typing and advanced TypeScript features for robust applications",
    projects: ["All major projects"]
  },
  {
    id: "nextjs",
    name: "Next.js",
    category: "frontend",
    level: "advanced",
    years: 2,
    description: "Full-stack React framework with SSR and optimization",
    projects: ["Portfolio Website", "Trading Dashboard"]
  },
  {
    id: "tailwind",
    name: "Tailwind CSS",
    category: "frontend",
    level: "expert",
    years: 3,
    description: "Utility-first CSS framework for rapid UI development",
    projects: ["All projects"]
  },

  // Backend
  {
    id: "nodejs",
    name: "Node.js",
    category: "backend",
    level: "advanced",
    years: 3,
    description: "Server-side JavaScript with Express and Fastify",
    projects: ["Trading Dashboard API", "DeFi Backend"]
  },
  {
    id: "python",
    name: "Python",
    category: "backend",
    level: "expert",
    years: 5,
    description: "Data analysis, ML, and backend development",
    projects: ["ML Prediction Model", "Data Analysis Tools"]
  },
  {
    id: "postgresql",
    name: "PostgreSQL",
    category: "backend",
    level: "advanced",
    years: 3,
    description: "Relational database design and optimization",
    projects: ["Trading Dashboard", "User Management"]
  },
  {
    id: "redis",
    name: "Redis",
    category: "backend",
    level: "intermediate",
    years: 2,
    description: "Caching and session management",
    projects: ["Trading Dashboard", "Real-time Features"]
  },

  // Blockchain
  {
    id: "solidity",
    name: "Solidity",
    category: "blockchain",
    level: "expert",
    years: 3,
    description: "Smart contract development and DeFi protocols",
    projects: ["DeFi Protocol", "NFT Marketplace"]
  },
  {
    id: "web3",
    name: "Web3.js",
    category: "blockchain",
    level: "advanced",
    years: 3,
    description: "Ethereum blockchain interaction and dApp development",
    projects: ["DeFi Protocol", "Wallet Integration"]
  },
  {
    id: "hardhat",
    name: "Hardhat",
    category: "blockchain",
    level: "advanced",
    years: 2,
    description: "Smart contract development and testing framework",
    projects: ["DeFi Protocol", "Token Contracts"]
  },
  {
    id: "ethers",
    name: "Ethers.js",
    category: "blockchain",
    level: "intermediate",
    years: 1,
    description: "Modern Ethereum library for dApp development",
    projects: ["Recent DeFi Projects"]
  },

  // Data & ML
  {
    id: "tensorflow",
    name: "TensorFlow",
    category: "data",
    level: "advanced",
    years: 2,
    description: "Deep learning and neural network development",
    projects: ["Crypto Prediction Model", "Sentiment Analysis"]
  },
  {
    id: "pandas",
    name: "Pandas",
    category: "data",
    level: "expert",
    years: 4,
    description: "Data manipulation and analysis",
    projects: ["All ML Projects", "Financial Analysis"]
  },
  {
    id: "numpy",
    name: "NumPy",
    category: "data",
    level: "expert",
    years: 4,
    description: "Numerical computing and array operations",
    projects: ["All ML Projects", "Mathematical Models"]
  },
  {
    id: "scikit-learn",
    name: "Scikit-learn",
    category: "data",
    level: "advanced",
    years: 3,
    description: "Machine learning algorithms and preprocessing",
    projects: ["Crypto Prediction", "Risk Models"]
  },

  // Finance
  {
    id: "quantitative-analysis",
    name: "Quantitative Analysis",
    category: "finance",
    level: "advanced",
    years: 3,
    description: "Financial modeling and risk assessment",
    projects: ["DeFi Risk Models", "Trading Strategies"]
  },
  {
    id: "portfolio-optimization",
    name: "Portfolio Optimization",
    category: "finance",
    level: "intermediate",
    years: 2,
    description: "Modern portfolio theory and optimization algorithms",
    projects: ["Trading Dashboard", "Yield Farming"]
  },
  {
    id: "risk-management",
    name: "Risk Management",
    category: "finance",
    level: "advanced",
    years: 3,
    description: "Financial risk assessment and mitigation strategies",
    projects: ["DeFi Protocol", "Trading Systems"]
  },

  // Tools
  {
    id: "git",
    name: "Git",
    category: "tools",
    level: "expert",
    years: 5,
    description: "Version control and collaborative development",
    projects: ["All projects"]
  },
  {
    id: "docker",
    name: "Docker",
    category: "tools",
    level: "advanced",
    years: 2,
    description: "Containerization and deployment",
    projects: ["ML Models", "Microservices"]
  },
  {
    id: "aws",
    name: "AWS",
    category: "tools",
    level: "intermediate",
    years: 2,
    description: "Cloud infrastructure and services",
    projects: ["Production Deployments"]
  },
  {
    id: "vscode",
    name: "VS Code",
    category: "tools",
    level: "expert",
    years: 4,
    description: "Primary development environment",
    projects: ["All projects"]
  }
]

export const skillCategories: SkillCategory[] = [
  {
    id: "frontend",
    name: "Frontend Development",
    description: "Modern web technologies for building user interfaces",
    skills: skills.filter(skill => skill.category === 'frontend')
  },
  {
    id: "backend",
    name: "Backend Development",
    description: "Server-side technologies and database management",
    skills: skills.filter(skill => skill.category === 'backend')
  },
  {
    id: "blockchain",
    name: "Blockchain & Web3",
    description: "Decentralized applications and smart contract development",
    skills: skills.filter(skill => skill.category === 'blockchain')
  },
  {
    id: "data",
    name: "Data Science & ML",
    description: "Machine learning, data analysis, and predictive modeling",
    skills: skills.filter(skill => skill.category === 'data')
  },
  {
    id: "finance",
    name: "Finance & Trading",
    description: "Financial modeling, quantitative analysis, and risk management",
    skills: skills.filter(skill => skill.category === 'finance')
  },
  {
    id: "tools",
    name: "Tools & DevOps",
    description: "Development tools, version control, and deployment",
    skills: skills.filter(skill => skill.category === 'tools')
  }
]

export const getSkillsByCategory = (categoryId: string) => {
  return skills.filter(skill => skill.category === categoryId)
}

export const getFeaturedSkills = () => {
  return skills.filter(skill => skill.level === 'expert' || skill.level === 'advanced')
}

export const getSkillsByLevel = (level: Skill['level']) => {
  return skills.filter(skill => skill.level === level)
}
